/**
 * Global user types
 */

import { Country, Language } from '../config';
import { FormattedUserAnnouncementSchema } from './announcement.types';
import { OrderShippingMethodType, OrderShippingType } from './order.types';
import { ProductType } from './product.types';
import { ShopImageType, ShopType } from './shop.types';

export type LDFlagValue = any;

export interface LDFlagSet {
  [key: string]: LDFlagValue;
}

export type UserBankAccountType = {
  accountName: string;
  accountNumber: string;
  bankExtra?: string;
  bankName: string;
  branchCode: number;
};

export type UserType = {
  id: number;
  name: string;
  firstName?: string;
  lastName?: string;
  picture?: string;
  title: string;
  email?: string;
  emailConfirmedAt: string | null;
  phone?: string;
  role?: string;
  bankAccount: UserBankAccountType;
  address?: UserAddressType;
  featured_score?: number;
  shop: ShopType;
  products: ProductType[];
  profilePicture: ShopImageType;
  profile_picture?: ShopImageType;
  isLoggedIn?: boolean;
  lastActive?: string;
  active?: boolean;
  first_name: string;
  last_name: string;
  likeCount: number;
  hasLiked: boolean;
  showEnableMessenger?: boolean;
  isMobileUser?: boolean;
  facebook_id?: string;
  flags?: LDFlagSet;
  is_admin: boolean;
  isAdmin: boolean;
  pinnedAt: boolean;
  likedProducts: { [productId: number]: boolean };
  shippingPrefillInfo: {
    [key in OrderShippingMethodType | `${OrderShippingMethodType}_seller`]: OrderShippingType;
  } & {
    lastOrderShippingMethod?: string | null;
    lastOrderShippingMethods?: string[] | null;
    prefillSources?: Record<string, 'purchase' | 'sale' | 'closed' | 'unavailable'>;
  };
  paymentPrefillInfo?: {
    lastPaymentProvider?: string | null;
    lastSubPaymentProvider?: string | null;
    usedPaymentMethods?: { provider: string; subProvider?: string | null }[] | null;
  };
  referralCode: string;
  referredBy: number;
  referredAt: Date;
  dac7_information: null | {
    data: IBusinessEntityData | IPrivatePersonData;
    accountType: UserAccountType;
  };
  dac7_required: boolean;
  dac7_required_at: Date;
  dac7_filled: boolean;
  dac7_filled_at: Date;
  dac7_last_notification_at: Date;
  hasUsedSavedSearch?: boolean;
  isAppUser?: boolean;
  termsAccepted?: boolean;
  onboardingMobileCompleted?: boolean;
  onboardingWebCompleted?: boolean;
  reConsent?: boolean;
  reConsentAt?: Date;
  requiresTermsAcceptance?: boolean;
  termsAcceptedAt?: boolean;
  marketingEmailNotificationsAt?: Date;
  consentMarketing?: boolean | null;
  consentAnalytics?: boolean | null;
  consentMarketingCommunications?: boolean | null;
  consentErrorTracking?: boolean | null;
  isPhoneVerified?: boolean;
  language?: Language;
  country: Country;
  globalId: string;
  announcements?: FormattedUserAnnouncementSchema[];
  walletFrozen?: boolean;
};

export type AddressSourceType = 'autocomplete' | 'manual';

export type UserAddressType = {
  city: string;
  suburb: string;
  postalCode: string;
  streetNumber: string;
  streetName: string;
  county?: string;
  zip?: string;
  country?: string;
  otherDetailsForCourier?: string;
  latitude?: number | null;
  longitude?: number | null;
  source?: AddressSourceType;
};

export type UserContactDetailsType = {
  phone: string;
  city: string;
  suburb: string;
  postalCode: string;
  streetNumber: string;
  streetName: string;
  otherDetailsForCourier?: string;
};

export type UserContactType = {
  phone: string;
  address?: UserAddressType;
  isProfile?: boolean;
  addressEntryPoint?: 'checkout' | 'settings' | 'listing';
  checkoutVisitId?: string;
};

export type UpdateUserBankAccountType = {
  bankAccount: UserBankAccountType;
  phone?: string;
  isProfile?: boolean;
  isPayout?: boolean;
};

export type UpdateUserEmailType = {
  email: string;
  emailConfirmed?: boolean;
};

enum MessengerEvents {
  Rendered = 'rendered',
  Clicked = 'clicked',
  Not_you = 'not_you',
  Opt_in = 'opt_in',
}

export type UserMessengerSubscriptionResponse = {
  event: MessengerEvents;
  ref: string;
  is_after_option: boolean;
  pluginID?: undefined;
};

export interface IPrivatePersonData {
  fullName: string;
  dateOfBirth: string;
  residencyAddress: string;
  residencyCountry: string;
  idCode?: string;
  vat?: string;
}

export const PrivatePersonDac7FormField: Record<keyof IPrivatePersonData, keyof IPrivatePersonData> = {
  fullName: 'fullName',
  dateOfBirth: 'dateOfBirth',
  residencyAddress: 'residencyAddress',
  residencyCountry: 'residencyCountry',
  idCode: 'idCode',
  vat: 'vat',
} as const;

export const BusinessDac7FormField: Record<keyof IBusinessEntityData, keyof IBusinessEntityData> = {
  name: 'name',
  registrationAddress: 'registrationAddress',
  registryCode: 'registryCode',
  operatingCountry: 'operatingCountry',
  vat: 'vat',
} as const;

export const privatePersonStrictlyRequiredFields = [
  PrivatePersonDac7FormField.fullName,
  PrivatePersonDac7FormField.dateOfBirth,
  PrivatePersonDac7FormField.residencyAddress,
  PrivatePersonDac7FormField.residencyCountry,
];

export const businessStrictlyRequiredFields = [
  BusinessDac7FormField.name,
  BusinessDac7FormField.registryCode,
  BusinessDac7FormField.registrationAddress,
  BusinessDac7FormField.operatingCountry,
];

export interface IBusinessEntityData {
  name: string;
  registrationAddress: string;
  registryCode: string;
  operatingCountry: string;
  vat?: string;
}

export type UserAccountType = (typeof UserAccountType)[keyof typeof UserAccountType];

export const UserAccountType = {
  PRIVATE_PERSON: 'privatePerson',
  BUSINESS: 'business',
} as const;

export const DAC7FormRequiredFields = {
  [UserAccountType.BUSINESS]: businessStrictlyRequiredFields,
  [UserAccountType.PRIVATE_PERSON]: privatePersonStrictlyRequiredFields,
};

export type Dac7FormErrorType = Record<keyof IBusinessEntityData | keyof IPrivatePersonData, string> | null;

export const CountryPropertyType = {
  PRIVATE_PERSON: PrivatePersonDac7FormField.residencyCountry,
  BUSINESS: BusinessDac7FormField.operatingCountry,
} as const;
